/**
 * Site entry point.
 *
 * Order of business, and the order matters:
 *   1. Reveals. Cheap, and the page is readable the moment this runs.
 *   2. Tier probe. Decides whether there is a 3D layer at all.
 *   3. The scene, lazily, after the content has had its chance to paint.
 *
 * Nothing in the sales content depends on any of it. If the WebGL layer never
 * boots — old browser, blocked script, exhausted GPU — the page is still a
 * complete, readable, purchasable product page. The book is decoration on top
 * of a document, not the document itself.
 *
 * Debug parameters:
 *   ?debug=1     show the diagnostic overlay
 *   ?tier=0..3   force a quality tier
 *   ?s=0.42      pin the story at a position, ignoring scroll
 *   ?scrollTo=.5 jump to a fraction of the story through the real scroll chain
 */

import {
  detectTier,
  tierOverride,
  TIER,
  TIER_NAME,
  TIER_SETTINGS,
  QualityGovernor,
} from './core/capability.js';
import { initReveals } from './core/reveal.js';
import './styles/site.css';

const base = import.meta.env.BASE_URL;
const params = new URLSearchParams(location.search);
const debug = params.has('debug');

// ?flat=1 collapses the eight viewport-tall beats into a single stacked
// document and hides the 3D layer. Purely a proofing aid: headless browsers
// cannot screenshot a scrolled page, so this is the only way to see the whole
// content layer at once.
if (params.has('flat')) document.body.classList.add('is-flat');

initReveals();

const probe = detectTier();
const tierStart = tierOverride() ?? probe.tier;

const canvas = document.getElementById('scene');
const fallback = document.getElementById('fallback');
const hud = document.getElementById('hud');

if (tierStart === TIER.STATIC) {
  showStatic(tierStart, probe.reasons);
} else {
  // The cover is on screen from the first paint, as a plain image, and the 3D
  // layer replaces it once it is genuinely ready.
  //
  // Two problems solved by the same move. Booting three.js during page load
  // blocks the main thread — 1.76s of blocking time under Lighthouse's 4x CPU
  // throttle, which is roughly a mid-range Android — and until it finished the
  // hero had no book in it at all. Now the network fetch starts immediately
  // while the expensive construction waits for idle, and the reader is looking
  // at a cover the whole time.
  showPoster();
  const modules = loadSceneModules(); // network starts now
  whenIdle(() => {
    boot(modules).catch((err) => {
      console.error('[site] 3D layer failed to start; keeping the static cover.', err);
      showStatic(TIER.STATIC, ['3D layer failed to start']);
    });
  });
}

/** Defer to the first idle moment, but never longer than the timeout. */
function whenIdle(fn) {
  if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 });
  else setTimeout(fn, 300);
}

function showPoster() {
  // The markup already carries the image and it is visible by default; this
  // only matters if something hid it earlier.
  fallback.hidden = false;
}

function loadSceneModules() {
  return Promise.all([
    import('./scene/Stage.js'),
    import('./scene/Book.js'),
    import('./scene/PageTextures.js'),
    import('./scene/Choreography.js'),
    import('./core/scroll.js'),
    import('./core/pointer.js'),
  ]);
}

function showStatic(tier, reasons) {
  canvas.hidden = true;
  fallback.hidden = false;
  // The story is eight full-viewport sections because each one is a beat in an
  // animation. With no animation there are no beats — just eight screens of
  // mostly empty space between the reader and the price. Collapse it into an
  // ordinary, tight landing page.
  document.body.classList.add('is-static');
  document.body.dataset.tier = TIER_NAME[tier];
  if (debug) {
    hud.hidden = false;
    hud.innerHTML =
      `<div class="line"><span>tier</span><b>${TIER_NAME[tier]}</b></div>` +
      `<div class="note">${reasons.join('<br>')}</div>`;
  }
}

async function boot(modules) {
  const [{ Stage }, { Book }, { PageTextures }, { Choreography }, { ScrollDriver }, { Pointer }] =
    await modules;

  const manifest = await fetch(`${base}pages/manifest.json`).then((r) => r.json());

  let tier = tierStart;
  let settings = TIER_SETTINGS[tier];
  let scroll = null;

  // Lets CSS back off effects the weakest devices cannot afford — see the
  // backdrop-filter rules in site.css.
  document.body.dataset.tier = TIER_NAME[tier];

  const stage = new Stage({
    canvas,
    settings,
    onContextLost: () => {
      console.warn('[site] webglcontextlost — reverting to the static cover.');
      scroll?.destroy();
      showStatic(TIER.STATIC, ['WebGL context lost']);
    },
  });

  let textures = new PageTextures(manifest, settings.textureTier, stage.renderer);
  const book = new Book({ manifest, textures, settings });
  stage.scene.add(book.group);

  const pointer = new Pointer();
  const choreography = new Choreography({ stage, book, pointer });

  const pinned = params.has('s') ? Number(params.get('s')) : null;
  if (pinned !== null && Number.isFinite(pinned)) {
    choreography.apply(Math.min(1, Math.max(0, pinned)));
  } else {
    scroll = new ScrollDriver({
      trigger: document.getElementById('story'),
      onProgress: (p) => choreography.apply(p),
      smooth: true,
    });

    if (params.has('scrollTo')) {
      const f = Math.min(1, Math.max(0, Number(params.get('scrollTo')) || 0));
      requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        scroll.lenis.scrollTo(max * f, { immediate: true });
      });
    }
  }

  const governor = new QualityGovernor({
    tier,
    onChange: (next) => {
      tier = next;
      if (next === TIER.STATIC) {
        stage.stop();
        scroll?.destroy();
        showStatic(TIER.STATIC, ['could not hold a usable frame rate']);
        return;
      }
      settings = TIER_SETTINGS[next];
      document.body.dataset.tier = TIER_NAME[next];
      stage.applySettings(settings);
      const older = textures;
      textures = new PageTextures(manifest, settings.textureTier, stage.renderer);
      book.applySettings(settings, textures);
      older.dispose();
    },
  });

  stage.onFrame((dt, now) => {
    scroll?.raf(now);
    pointer.update(dt);
    choreography.place();
    governor.tick(now);
    if (debug) updateHud();
  });

  stage.start();

  // Hold the poster until the cover texture is actually on the GPU. Swapping
  // any earlier trades a finished image for a blank rectangle, which is a
  // worse hero than the one we started with.
  await textures.preload([1, 2]);
  canvas.hidden = false;
  stage.resize(); // the canvas had no layout box while hidden
  choreography.place();
  requestAnimationFrame(() => {
    // One frame of overlap so the canvas has painted before the poster goes.
    fallback.hidden = true;
    document.body.classList.add('scene-ready');
  });

  // --- diagnostics --------------------------------------------------------
  function updateHud() {
    const now = performance.now();
    if (now - (updateHud.at ?? 0) < 300) return;
    updateHud.at = now;
    const s = textures.stats();
    hud.innerHTML =
      `<div class="line"><span>tier</span><b>${TIER_NAME[tier]}</b></div>` +
      `<div class="line"><span>fps</span><b>${governor.lastFps ? governor.lastFps.toFixed(0) : '—'}</b></div>` +
      `<div class="line"><span>textures</span><b>${s.resident} @ ${s.tier}</b></div>` +
      `<div class="line"><span>story</span><b>${choreography.progress.toFixed(2)}</b></div>` +
      `<div class="line"><span>book</span><b>${book.progress.toFixed(2)}</b></div>` +
      `<div class="line"><span>beat</span><b>${choreography.nearestBeat() + 1}</b></div>`;
  }

  if (debug) {
    hud.hidden = false;
    Object.assign(window, { book, stage, choreography, pointer, textures });
  }
}